import { useTranslation, formatPrice, getOrderAmountDisplay } from '@dukanti/shared';
import type { Order } from '@dukanti/shared';

export interface OrderDetailTotalsProps {
  readonly order: Order;
}

/**
 * F-022: Order totals section for the order detail page.
 *
 * Shows the subtotal. Uses Fraunces (font-display) for the amount to
 * match the warm design system.
 */
export function OrderDetailTotals({ order }: OrderDetailTotalsProps): React.JSX.Element {
  const { t, lang } = useTranslation();

  const amount = getOrderAmountDisplay(order);
  return (
    <section
      data-testid="order-detail-totals"
      className="border-border bg-card shadow-admin-card rounded-lg border p-6"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground text-sm font-medium">
          {t('admin.orders.detail.subtotal')}
        </span>
        <span
          data-testid="order-detail-subtotal"
          className="font-display text-saffron ms-auto text-base font-semibold"
        >
          {formatPrice(order.subtotal_cents, lang)}
        </span>
      </div>
      {amount.confirmed && order.delivery_fee_cents !== null ? (
        <div className="mt-3 flex justify-between gap-4 text-sm">
          <span className="text-muted-foreground">
            {t(
              order.delivery_method === 'pickup'
                ? 'fulfillment.freePickup'
                : 'fulfillment.deliveryFee',
            )}
          </span>
          <span>{formatPrice(order.delivery_fee_cents, lang)}</span>
        </div>
      ) : null}
      <div className="border-border mt-4 flex justify-between gap-4 border-t pt-4">
        <span className="text-muted-foreground text-sm">
          {t(amount.confirmed ? 'checkout.total' : 'fulfillment.legacySubtotal')}
        </span>
        <span
          data-testid="order-detail-total"
          className="font-display text-foreground font-semibold"
        >
          {formatPrice(amount.amountCents, lang)}
        </span>
      </div>
    </section>
  );
}
